import { Component, inject, OnInit, signal, Signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { LabelComponent } from '@app/shared/components/label/label.component';
import { TagComponent } from '@app/shared/components/tag/tag.component';
import { Tag } from '@app/shared/types/models/tag/tag.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { TranslatePipe } from '@ngx-translate/core';

export interface SelectTagsDialogData {
  tags: Signal<Tag[]>;
  chosenTags: Tag[];
  onAddTag?: () => void;
  onEditTag?: (tag: Tag) => void;
}

@Component({
  selector: 'select-tags-modal',
  templateUrl: './select-tags-dialog.component.html',
  imports: [
    MatIconModule,
    TranslatePipe,
    MatDialogModule,
    MatButtonModule,
    LabelComponent,
    MatSelectModule,
    MatCheckboxModule,
    UiDialogComponent,
    UiButtonComponent,
    TagComponent
  ]
})
export class SelectTagsDialogComponent implements OnInit {
  private readonly data: SelectTagsDialogData = inject(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<SelectTagsDialogComponent> = inject(MatDialogRef);

  public tags: Signal<Tag[]> = signal([]);
  public chosenTags: Tag[] = [];

  public selectedTags = signal<Set<string>>(new Set());

  public ngOnInit(): void {
    this.tags = this.data.tags;
    this.chosenTags = this.data.chosenTags;
    const initialSelected = new Set<string>(this.chosenTags.map(t => t.id));
    this.selectedTags.set(initialSelected);
  }

  public isTagSelected(tagId: string): boolean {
    return this.selectedTags().has(tagId);
  }

  public onTagToggle(tagId: string, checked?: boolean): void {
    if (checked === undefined) {
      checked = !this.isTagSelected(tagId);
    }

    this.selectedTags.update(prev => {
      const selected = new Set(prev);
      if (checked) {
        selected.add(tagId);
      } else {
        selected.delete(tagId);
      }
      return selected;
    });
  }

  protected handleSaveClick(): void {
    const selected = this.tags().filter(tag => this.selectedTags().has(tag.id));
    this.dialogRef.close(selected);
  }

  protected addTag(): void {
    if (this.data.onAddTag) {
      this.data.onAddTag();
    }
  }

  protected editTag(tag: Tag, event: MouseEvent): void {
    event.stopPropagation();
    if (this.data.onEditTag) {
      this.data.onEditTag(tag);
    }
  }
}
